/**
 * WASSER Admin - Theme Provider with Dark Mode Toggle
 *
 * Использование:
 * import { ThemeProviderWithMode, useThemeMode } from '@/theme/ThemeProvider';
 *
 * // В App.jsx:
 * <ThemeProviderWithMode>
 *   <App />
 * </ThemeProviderWithMode>
 *
 * // В компоненте для toggle:
 * const { mode, toggleMode } = useThemeMode();
 */
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getTheme } from './getTheme';

// Storage key для сохранения выбора
const THEME_STORAGE_KEY = 'wasser-admin-theme-mode';

// Получить начальный режим
const getInitialMode = () => {
  // 1. Проверяем localStorage
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }

    // 2. Проверяем системные настройки
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  }

  // 3. По умолчанию - light
  return 'light';
};

// Context для режима темы
const ThemeModeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
  isDark: false,
});

/**
 * Hook для доступа к режиму темы
 */
export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProviderWithMode');
  }
  return context;
};

/**
 * Theme Provider с поддержкой dark mode
 */
export const ThemeProviderWithMode = ({ children, defaultMode }) => {
  const [mode, setModeState] = useState(() => defaultMode || getInitialMode());

  // Создаём тему при изменении режима
  const theme = useMemo(() => getTheme(mode), [mode]);

  // Сохраняем выбор в localStorage
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);

    // Также обновляем meta tag для цвета строки состояния на мобильных
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        mode === 'dark' ? '#0F1115' : '#ffffff'
      );
    }
  }, [mode]);

  // Слушаем системные изменения темы
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Применяем системную тему только если нет сохранённого выбора
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setModeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Функции управления режимом
  const toggleMode = () => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setMode = (newMode) => {
    if (newMode === 'light' || newMode === 'dark') {
      setModeState(newMode);
    }
  };

  const contextValue = useMemo(
    () => ({
      mode,
      toggleMode,
      setMode,
      isDark: mode === 'dark',
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeProviderWithMode;
